import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Row, Col, Card, CardBody, Input, Button, Badge, Spinner, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Progress, Modal } from 'reactstrap';
import { Lock, Plus, Check, Trash2, X, LayoutGrid, ArrowRight, MoreVertical, Edit2, ChevronDown, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpaceStore } from '../../../stores/Assets/space';
import { useMenuStore } from '../../../stores/Assets/menu';
import { useKitchenStore } from '../../../stores/Assets/kitchen';
import { useUsageStore } from '../../../stores/Usage';
import { useExperience } from '../../../stores/Experience/Experience';

// -- Constants & Helpers --
const THEME_COLOR = '#c59253';

const getMenuMeta = (title) => {
    const initials = title.substring(0, 2).toUpperCase();
    const colors = ['#334155', '#475569', '#64748b', '#94a3b8', '#cbd5e1', '#0f172a'];
    let hash = 0;
    for (let i = 0; i < title.length; i++) hash = title.charCodeAt(i) + ((hash << 5) - hash);
    const color = colors[Math.abs(hash) % colors.length];
    return { initials, color };
};

// 1. Branch Tabs (Read-Only)
const BranchTabs = ({ sites, activeTab, setActiveTab }) => {
    return (
        <div className="border-bottom bg-white sticky-top shadow-sm" style={{ zIndex: 10, top: 0 }}>
            <div className="d-flex overflow-auto px-3 py-2 no-scrollbar" style={{ gap: '1rem' }}>
                {sites.map(site => (
                    <div
                        key={site.site_id}
                        onClick={() => setActiveTab(site.site_id)}
                        className={`d-flex align-items-center gap-2 px-3 py-2 rounded-pill cursor-pointer transition-all ${activeTab === site.site_id ? 'text-white shadow' : 'bg-light text-muted hover-bg-gray'
                            }`}
                        style={{
                            whiteSpace: 'nowrap',
                            minWidth: 'fit-content',
                            cursor: 'pointer',
                            backgroundColor: activeTab === site.site_id ? THEME_COLOR : undefined
                        }}
                    >
                        {site.image_url ? (
                            <img src={site.image_url} alt="" className="rounded-circle" style={{ width: '20px', height: '20px', objectFit: 'cover' }} />
                        ) : (
                            <div
                                className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${activeTab === site.site_id ? 'bg-white text-dark' : 'bg-secondary text-white'}`}
                                style={{ width: '20px', height: '20px', fontSize: '10px' }}
                            >
                                {site.name.charAt(0)}
                            </div>
                        )}
                        <span className="fw-semibold">{site.name}</span>
                        {activeTab === site.site_id && <Lock size={12} className="opacity-50" />}
                    </div>
                ))}
            </div>
        </div>
    );
};

const SpaceCard = ({ name, isNew, onEdit, onDelete, isSelected, onToggle, loading }) => (
    <div
        className={`d-flex align-items-center gap-3 p-2 px-3 rounded-4 shadow-sm transition-all bg-white border position-relative`}
        onClick={!loading ? onToggle : undefined}
        style={{
            borderColor: isSelected ? THEME_COLOR : '#f1f5f9',
            cursor: onToggle && !loading ? 'pointer' : 'default',
            width: 'fit-content',
            minWidth: '140px'
        }}
    >
        <div className="d-flex align-items-center gap-2 flex-grow-1 overflow-hidden">
            {onToggle && (
                <div
                    className={`rounded-circle border d-flex align-items-center justify-content-center transition-all ${isSelected ? 'text-white border-transparent' : 'bg-white'}`}
                    style={{
                        width: 18,
                        height: 18,
                        minWidth: 18,
                        backgroundColor: isSelected ? THEME_COLOR : undefined
                    }}
                >
                    {isSelected && <Check size={10} />}
                </div>
            )}
            <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                {isNew && (
                    <Badge
                        color="soft-success"
                        className="bg-success bg-opacity-10 rounded-4 position-absolute text-success border-0 w-auto align-self-start"
                        style={{ fontSize: '7px', top: '5px', left: '40px', padding: '2px 5px' }}
                    >
                        NEW
                    </Badge>
                )}

                <span className="fw-bold text-dark text-truncate" style={{ fontSize: '13px' }}>
                    {name}
                </span>
            </div>
        </div>

        <div className="d-flex gap-1" onClick={e => e.stopPropagation()}>
            {loading ? (
                <Spinner size="sm" color="secondary" style={{ width: '13px', height: '13px', borderWidth: '2px' }} />
            ) : (
                <>
                    {onEdit && (
                        <Button color="link" className="p-1 text-muted opacity-50 hover-opacity-100" onClick={onEdit}>
                            <Edit2 size={13} />
                        </Button>
                    )}
                    {onDelete && (
                        <Button color="link" className="p-1 text-danger opacity-50 hover-opacity-100" onClick={onDelete}>
                            <Trash2 size={13} />
                        </Button>
                    )}
                </>
            )}
        </div>
    </div>
);

const isToggleable = (onToggle) => typeof onToggle === 'function';

// 6. Next Branch Prompt
const NextBranchPrompt = ({ nextSite, onSwitch }) => {
    if (!nextSite) return null;
    return (
        <div className="mt-4 p-4 rounded-4 bg-white border border-dashed text-center w-100 position-relative overflow-hidden">
            <div className="position-absolute top-0 start-0 w-100 h-100 opacity-10 bg-light" />
            <div className="position-relative z-10">
                <p className="text-muted small fw-bold text-uppercase tracking-wider mb-3">Ready for the next location?</p>
                <Button
                    color="white"
                    className="rounded-pill px-4 py-2 border shadow-sm d-inline-flex align-items-center gap-2 hover-up transition-all"
                    onClick={onSwitch}
                >
                    <span className="text-muted">Configure:</span>
                    <span className="fw-bold text-dark">{nextSite.name}</span>
                    <ArrowRight size={16} className="text-primary ms-1" />
                </Button>
            </div>
        </div>
    );
};

// ... inside EmptyState ...
const EmptyState = ({ onGenerate, existingCount, existingSpaces, onDeleteExisting, nextSite, onSwitchToNext }) => (
    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center h-100 p-3 overflow-auto">
        <div className="mb-4 p-4 rounded-circle bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: '80px', height: '80px' }}>
            <LayoutGrid size={32} className="text-muted opacity-50" />
        </div>
        {/* ... title and description ... */}
        <h4 className="fw-bold text-dark mb-2">
            {existingCount > 0 ? `Add more spaces to this branch` : `This branch has no spaces yet`}
        </h4>
        <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '300px' }}>
            {existingCount > 0
                ? `You already have ${existingCount} spaces configured. Create more for your customers.`
                : `Start by adding spaces for customers to order from.`
            }
        </p>

        <div className="d-flex flex-column flex-sm-row gap-3 w-100 justify-content-center px-3 mb-4">
            <Button className="rounded-pill px-5 py-2.5 fw-bold text-white border-0 shadow-lg" style={{ backgroundColor: THEME_COLOR }} onClick={onGenerate}>
                {existingCount > 0 ? 'Generate More Spaces' : 'Generate Spaces'}
            </Button>
        </div>

        {existingCount > 0 && (
            <div className="w-100 animate-fade-in text-start pt-4 border-top" style={{ maxWidth: '600px' }}>
                <div className="d-flex align-items-start justify-content-start gap-2 mb-3 mt-2">
                    <span className="text-muted small fw-bold text-uppercase tracking-wider" style={{ fontSize: '10px' }}>Existing Spaces</span>
                    <Badge pill color="light" className="text-muted border ms-1 fw-bold">{existingCount}</Badge>
                </div>
                <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-start mb-4">
                    {existingSpaces.map((space) => (
                        <SpaceCard
                            key={space.space_id}
                            name={space.name}
                            onDelete={() => onDeleteExisting(space.space_id)}
                        />
                    ))}
                </div>

                {nextSite && (
                    <NextBranchPrompt nextSite={nextSite} onSwitch={onSwitchToNext} />
                )}
            </div>
        )}
    </div>
);

// 3. Generation View
const GenerationView = ({ onCancel, onConfirm, siteName }) => {
    const terminology = useExperience.getState().getTerminology();
    const [count, setCount] = useState(10);
    const [prefix, setPrefix] = useState(terminology.space || 'Table');

    const handleConfirm = () => {
        const names = [];
        for (let i = 1; i <= count; i++) {
            names.push(`${prefix} ${i}`);
        }
        onConfirm(names);
    };

    return (
        <div className="h-100 d-flex flex-column bg-white">
            <div className="p-3 border-bottom d-flex align-items-center justify-content-between bg-white sticky-top">
                <div>
                    <h5 className="fw-bold mb-0">Generate Spaces</h5>
                    <p className="text-muted small mb-0">{siteName}</p>
                </div>
                <Button color="link" className="text-muted p-0" onClick={onCancel}><X size={24} /></Button>
            </div>

            <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center p-4">
                <div className="w-100" style={{ maxWidth: '400px' }}>
                    <div className="text-center mb-5">
                        <label className="text-muted fw-bold text-uppercase tracking-wider mb-3 d-block" style={{ fontSize: '12px' }}>How many spaces?</label>
                        <div className="d-flex align-items-center justify-content-center gap-4">
                            <Button
                                color="light"
                                className="rounded-circle d-flex align-items-center justify-content-center border shadow-sm"
                                style={{ width: '50px', height: '50px', fontSize: '24px' }}
                                onClick={() => setCount(Math.max(1, count - 1))}
                            >
                                -
                            </Button>
                            <Input
                                type="number"
                                className="text-center fw-bold border-0 bg-transparent p-0"
                                style={{ fontSize: '72px', width: '150px', height: 'auto', outline: 'none', boxShadow: 'none' }}
                                value={count}
                                onChange={(e) => setCount(Math.max(1, parseInt(e.target.value) || 0))}
                            />
                            <Button
                                color="light"
                                className="rounded-circle d-flex align-items-center justify-content-center border shadow-sm"
                                style={{ width: '50px', height: '50px', fontSize: '24px' }}
                                onClick={() => setCount(count + 1)}
                            >
                                +
                            </Button>
                        </div>
                    </div>

                    <div className="bg-light p-4 rounded-4 shadow-sm">
                        <label className="text-muted fw-bold text-uppercase tracking-wider mb-2 d-block" style={{ fontSize: '10px' }}>Space Prefix</label>
                        <Input
                            className="form-control-lg border-0 bg-white shadow-sm fw-semibold"
                            placeholder="e.g. Table, Office, Room..."
                            value={prefix}
                            onChange={(e) => setPrefix(e.target.value)}
                            style={{ borderRadius: '12px' }}
                        />
                        <p className="text-muted small mt-2 mb-0" style={{ fontSize: '11px' }}>
                            Spaces will be named: <strong>{prefix} 1, {prefix} 2...</strong>
                        </p>
                    </div>
                </div>
            </div>

            <div className="p-4 border-top bg-white sticky-bottom">
                <Button
                    block
                    size="lg"
                    className="w-100 rounded-pill py-3 shadow-lg fw-bold text-white border-0"
                    style={{ backgroundColor: THEME_COLOR }}
                    onClick={handleConfirm}
                    disabled={count <= 0}
                >
                    Create {count} Space{count > 1 ? 's' : ''}
                </Button>
            </div>
        </div>
    );
};

// SpacesSkeleton Component
const SpacesSkeleton = () => (
    <div className="h-100 d-flex flex-column">
        {/* Header Skeleton */}
        <div className="px-3 py-2 border-bottom bg-white d-flex align-items-center justify-content-between sticky-top">
            <div className="d-flex align-items-center gap-2">
                <div className="rounded-circle bg-light placeholder-glow" style={{ width: 18, height: 18 }}></div>
                <span className="placeholder col-3 bg-light rounded"></span>
            </div>
        </div>

        <div className="flex-grow-1 p-3 bg-light">
            <div className="max-w-xl mx-auto">
                <div className="mb-4">
                    <div className="d-flex align-items-center mb-2 px-1">
                        <span className="placeholder col-2 bg-secondary rounded" style={{ opacity: 0.2 }}></span>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {/* Card Skeletons */}
                        {[1, 2, 3].map(i => (
                            <div key={i} className="bg-white border rounded-4 p-2 px-3 shadow-sm d-flex align-items-center gap-2" style={{ width: 'auto', minWidth: '140px', height: '42px' }}>
                                <div className="placeholder col-8 bg-light rounded"></div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="d-flex align-items-start justify-content-start gap-2 mb-3 mt-4">
                        <span className="placeholder col-3 bg-secondary rounded" style={{ opacity: 0.2 }}></span>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {[1, 2, 3, 4, 5].map(i => (
                            <div key={`ex-${i}`} className="bg-white border rounded-4 p-2 px-3 shadow-sm d-flex align-items-center gap-2" style={{ width: 'auto', minWidth: '140px', height: '42px' }}>
                                <div className="placeholder col-6 bg-light rounded"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// 5. Space Manager (Editable Items)
const SpaceManager = ({ spaces, existingSpaces, onAdd, onDelete, onUpdate, onDeleteExisting, onUpdateExisting, updatingId, nextSite, onSwitchToNext }) => {
    // ... items state ...
    const [selected, setSelected] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingExistingId, setEditingExistingId] = useState(null);
    const [editValue, setEditValue] = useState("");
    const editInputRef = useRef(null);
    const [editLoadingId, setEditLoadingId] = useState(null);

    const [selectedExisting, setSelectedExisting] = useState([]);
    const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, target: null, type: null }); // type: 'new' or 'existing'

    // ... helper functions ...
    const toggleSelection = (idx) => {
        if (selected.includes(idx)) setSelected(selected.filter(i => i !== idx));
        else setSelected([...selected, idx]);
    };

    const handleSelectAll = () => {
        if (selected.length === spaces.length) setSelected([]);
        else setSelected(spaces.map((_, i) => i));
    };

    const toggleSelectionExisting = (id) => {
        if (selectedExisting.includes(id)) setSelectedExisting(selectedExisting.filter(i => i !== id));
        else setSelectedExisting([...selectedExisting, id]);
    };

    const handleSelectAllExisting = () => {
        const allIds = existingSpaces.map(s => s.space_id);
        if (selectedExisting.length === existingSpaces.length) setSelectedExisting([]);
        else setSelectedExisting(allIds);
    };

    const startEditing = (index, currentValue, e) => {
        e.stopPropagation();
        setEditingIndex(index);
        setEditingExistingId(null);
        setEditValue(currentValue);
        setTimeout(() => editInputRef.current?.focus(), 50);
    };

    const startEditingExisting = (id, currentValue, e) => {
        e.stopPropagation();
        setEditingExistingId(id);
        setEditingIndex(null);
        setEditValue(currentValue);
        setTimeout(() => editInputRef.current?.focus(), 50);
    };

    const saveEditing = async () => {
        if (editingIndex !== null) {
            onUpdate(editingIndex, editValue);
            setEditingIndex(null);
        } else if (editingExistingId !== null) {
            const idToUpdate = editingExistingId;
            const valueToUpdate = editValue;

            // 1. Show Spinner (Switch to Card View)
            setEditLoadingId(idToUpdate);
            setEditingExistingId(null);

            // 2. Perform Update
            await onUpdateExisting(idToUpdate, valueToUpdate);

            // 3. Clear Spinner
            setEditLoadingId(null);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') saveEditing();
        if (e.key === 'Escape') {
            setEditingIndex(null);
            setEditingExistingId(null);
        }
    };

    const requestDelete = (target, type) => {
        setConfirmDelete({ isOpen: true, target, type });
    };

    const executeDelete = () => {
        const { target, type } = confirmDelete;
        if (type === 'new') {
            onDelete(target);
            setSelected([]);
        } else if (type === 'existing') {
            onDeleteExisting(target);
            setSelectedExisting([]);
        }
        setConfirmDelete({ isOpen: false, target: null, type: null });
    };

    return (
        <div className="h-100 d-flex flex-column">
            {/* Delete Confirmation Modal */}
            <Modal isOpen={confirmDelete.isOpen} toggle={() => setConfirmDelete({ ...confirmDelete, isOpen: !confirmDelete.isOpen })} centered size="sm">
                <div className="p-4 text-center">
                    <div className="mx-auto bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                        <Trash2 className="text-danger" size={24} />
                    </div>
                    <h5 className="fw-bold mb-2">Delete Space?</h5>
                    <p className="text-muted small mb-4">
                        Are you sure you want to delete {Array.isArray(confirmDelete.target) ? `${confirmDelete.target.length} spaces` : 'this space'}?
                        This action cannot be undone.
                    </p>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button color="light" className="border px-4" onClick={() => setConfirmDelete({ ...confirmDelete, isOpen: false })}>Cancel</Button>
                        <Button color="danger" className="px-4" onClick={executeDelete}>Delete</Button>
                    </div>
                </div>
            </Modal>

            <div className="px-3 py-2 border-bottom bg-white d-flex align-items-center justify-content-between sticky-top shadow-sm" style={{ top: 0, zIndex: 10 }}>
                <div className="d-flex align-items-center gap-2">
                    <h6 className="mb-0 fw-bold text-muted text-uppercase small">All Spaces</h6>
                    <Badge color="secondary" className="bg-opacity-10 text-dark">
                        {spaces.length + existingSpaces.length}
                    </Badge>
                </div>

                <div className="d-flex gap-2">
                    {selected.length + selectedExisting.length > 0 && (
                        <Button color="danger" size="sm" className="rounded-pill px-3 shadow-sm border-0" onClick={() => {
                            if (selected.length > 0) requestDelete(selected, 'new');
                            if (selectedExisting.length > 0) requestDelete(selectedExisting, 'existing');
                        }}>
                            Delete ({selected.length + selectedExisting.length})
                        </Button>
                    )}
                    <Button
                        outline
                        size="sm"
                        className="rounded-pill px-3 border-dashed"
                        style={{ borderColor: THEME_COLOR, color: THEME_COLOR }}
                        onClick={onAdd}
                    >
                        <Plus size={14} className="me-1" /> Add Spaces
                    </Button>
                </div>
            </div>

            <div className="flex-grow-1 overflow-auto p-3 bg-light custom-scrollbar">
                <div className="max-w-xl mx-auto pb-5">
                    <div className="d-flex flex-wrap gap-2">
                        {/* 1. New Spaces List */}
                        {spaces.map((space, i) => (
                            <div key={`new-${i}`}>
                                {editingIndex === i ? (
                                    <div className="bg-white border rounded-4 p-2 px-3 shadow-sm d-flex align-items-center gap-2" style={{ width: '200px' }}>
                                        <Input
                                            innerRef={editInputRef}
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onBlur={saveEditing}
                                            onKeyDown={handleKeyDown}
                                            className="form-control-sm border-0 p-0 m-0 fw-bold fs-6 shadow-none flex-grow-1"
                                            style={{ background: 'transparent' }}
                                        />
                                        <Check size={14} className="text-success cursor-pointer" onClick={saveEditing} />
                                    </div>
                                ) : (
                                    <SpaceCard
                                        name={space}
                                        isNew={true}
                                        isSelected={selected.includes(i)}
                                        onToggle={() => toggleSelection(i)}
                                        onEdit={(e) => startEditing(i, space, e)}
                                        onDelete={(e) => { e.stopPropagation(); requestDelete([i], 'new'); }}
                                    />
                                )}
                            </div>
                        ))}

                        {/* 2. Existing Spaces List */}
                        {existingSpaces.map((space) => (
                            <div key={space.space_id}>
                                {editingExistingId === space.space_id ? (
                                    <div className="bg-white border rounded-4 p-2 px-3 shadow-sm d-flex align-items-center gap-2" style={{ width: '200px' }}>
                                        <Input
                                            innerRef={editInputRef}
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onBlur={saveEditing}
                                            onKeyDown={handleKeyDown}
                                            className="form-control-sm border-0 p-0 m-0 fw-bold fs-6 shadow-none flex-grow-1"
                                            style={{ background: 'transparent' }}
                                            autoFocus
                                            disabled={updatingId === space.space_id}
                                        />
                                        <Check size={14} className="text-success cursor-pointer" onClick={saveEditing} />
                                    </div>
                                ) : (
                                    <SpaceCard
                                        name={space.name}
                                        onEdit={(e) => startEditingExisting(space.space_id, space.name, e)}
                                        onDelete={() => requestDelete(space.space_id, 'existing')}
                                        loading={updatingId === space.space_id || editLoadingId === space.space_id}
                                        isSelected={selectedExisting.includes(space.space_id)}
                                        onToggle={() => toggleSelectionExisting(space.space_id)}
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    {spaces.length === 0 && (!existingSpaces || existingSpaces.length === 0) && (
                        <div className="text-center py-5">
                            <p className="text-muted">No spaces found. Click "Add Spaces" to begin.</p>
                        </div>
                    )}

                    {nextSite && (
                        <NextBranchPrompt nextSite={nextSite} onSwitch={onSwitchToNext} />
                    )}
                </div>
            </div>
        </div>
    );
};

// -- Main Component --
const DistributionStep = ({ sites, onNext, onBack, initialSiteId, distributionData = {}, setDistributionData }) => {
    // distributionData is now the single source of truth passed from parent (index.js)
    const [activeTab, setActiveTab] = useState(null);
    const [viewMode, setViewMode] = useState('list');
    const [updatingId, setUpdatingId] = useState(null);
    const autoOpenRef = useRef(false);


    const { getSpaces, isLoading: loadingExisting } = useSpaceStore();
    const [existingSpacesMap, setExistingSpacesMap] = useState({});

    useEffect(() => {
        if (sites.length > 0) {
            // If initialized with a specific site, force it to be active
            if (initialSiteId && activeTab !== initialSiteId) {
                setActiveTab(initialSiteId);
                // setViewMode('generate'); // Logic moved to fetchExisting
            } else if (!activeTab) {
                // Fallback to first site if nothing selected
                setActiveTab(sites[0].site_id);
            }
        }
    }, [sites, initialSiteId, activeTab]);

    const { getMenus } = useMenuStore();
    const [menusMap, setMenusMap] = useState({});

    useEffect(() => {
        const fetchExisting = async () => {
            if (!activeTab) return;
            // Fetch spaces and menus for this specific site
            const [spacesData, menusData] = await Promise.all([
                getSpaces(activeTab, { page: 1, perPage: 100 }),
                getMenus(activeTab, 1, 100)
            ]);

            setExistingSpacesMap(prev => ({
                ...prev,
                [activeTab]: spacesData || []
            }));

            // Auto-open generate if no spaces exist AND no new spaces added content, and only once
            const hasExisting = spacesData && spacesData.length > 0;
            const hasNew = distributionData[activeTab] && distributionData[activeTab].length > 0;

            if (!hasExisting && !hasNew && !autoOpenRef.current && initialSiteId) {
                setViewMode('generate');
                autoOpenRef.current = true;
            }

            const menus = menusData?.menus || [];
            const mappedMenus = {};
            menus.forEach(m => { mappedMenus[m.menu_id] = m; });
            setMenusMap(prev => ({
                ...prev,
                [activeTab]: mappedMenus
            }));
        };
        fetchExisting();
    }, [activeTab]);

    const currentSpaces = distributionData[activeTab] || [];
    const existingSpaces = existingSpacesMap[activeTab] || [];
    const isFetching = loadingExisting && !existingSpacesMap[activeTab];

    const { fetchUsage, getLimitInfo } = useUsageStore();
    const limitInfo = getLimitInfo("spaces");

    useEffect(() => {
        fetchUsage();
    }, [fetchUsage]);

    const totalCount = Object.values(distributionData).reduce((acc, list) => acc + (list?.length || 0), 0);
    const totalInSpaces = (limitInfo?.current || 0) + totalCount;
    const isOverLimit = limitInfo?.limit !== 'Unlimited' && totalInSpaces > limitInfo?.limit;

    // Calculate Next Site for UX Prompt
    const currentSiteIndex = sites.findIndex(s => s.site_id === activeTab);
    const nextSite = currentSiteIndex >= 0 && currentSiteIndex < sites.length - 1 ? sites[currentSiteIndex + 1] : null;
    const handleSwitchToNext = () => setActiveTab(nextSite.site_id);

    // Check if any site has existing spaces
    const hasExistingSpaces = Object.values(existingSpacesMap).some(list => list?.length > 0);

    const activeSite = sites.find(s => s.site_id === activeTab);

    const handleGenerate = (newNamesList) => {
        setDistributionData({ ...distributionData, [activeTab]: [...currentSpaces, ...newNamesList] });
        setViewMode('list');
    };

    const handleDelete = (indices) => {
        const newArr = currentSpaces.filter((_, i) => !indices.includes(i));
        setDistributionData({ ...distributionData, [activeTab]: newArr });
    };

    const handleDeleteExisting = async (target) => {
        // target can be single ID or array of IDs
        const ids = Array.isArray(target) ? target : [target];
        if (ids.length === 0) return;

        const { deleteSpace } = useSpaceStore.getState();

        let successCount = 0;
        // Delete sequentially or parallel - parallel is better for UX but need to handle errors
        await Promise.all(ids.map(async (id) => {
            const success = await deleteSpace(id);
            if (success) successCount++;
        }));

        if (successCount > 0) {
            // Refresh existing spaces
            const spacesData = await getSpaces(activeTab, { page: 1, perPage: 100 });
            setExistingSpacesMap(prev => ({
                ...prev,
                [activeTab]: spacesData || []
            }));
        }
    };

    const handleUpdateExisting = async (spaceId, newName) => {
        if (!newName || !newName.trim()) return;

        // 1. Optimistic Update & Set Loading
        setUpdatingId(spaceId);

        const previousSpaces = existingSpacesMap[activeTab] || [];
        const optimisticSpaces = previousSpaces.map(s =>
            s.space_id === spaceId ? { ...s, name: newName } : s
        );

        setExistingSpacesMap(prev => ({
            ...prev,
            [activeTab]: optimisticSpaces
        }));

        try {
            const { updateSpace } = useSpaceStore.getState();
            const success = await updateSpace(spaceId, { name: newName }, activeTab);

            if (success) {
                // Refresh to ensure sync (optional if we trust optimistic, but good practice)
                const spacesData = await getSpaces(activeTab, { page: 1, perPage: 100 });
                setExistingSpacesMap(prev => ({
                    ...prev,
                    [activeTab]: spacesData || []
                }));
            } else {
                // Revert on failure
                setExistingSpacesMap(prev => ({
                    ...prev,
                    [activeTab]: previousSpaces
                }));
                console.error("Update failed");
            }
        } catch (e) {
            console.error(e);
            // Revert on error
            setExistingSpacesMap(prev => ({
                ...prev,
                [activeTab]: previousSpaces
            }));
        } finally {
            setUpdatingId(null);
        }
    };

    const handleUpdate = (index, newValue) => {
        if (!newValue || !newValue.trim()) return;
        const newArr = [...currentSpaces];
        newArr[index] = newValue;
        setDistributionData({ ...distributionData, [activeTab]: newArr });
    };

    const [submitting, setSubmitting] = useState(false);

    const handleFinalize = async () => {
        setSubmitting(true);
        try {
            onNext(distributionData);
        } catch (e) {
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleBackInternal = () => {
        if (onBack) onBack();
    };

    return (
        <div className="bg-white h-100 d-flex flex-column position-relative overflow-hidden">
            {/* <BranchTabs sites={sites} activeTab={activeTab} setActiveTab={setActiveTab} /> */}
            <div className="py-3 px-4 border-bottom bg-white d-flex align-items-center justify-content-between">
                <div>
                    <h5 className="fw-bold mb-0">{activeSite?.name || 'Manage Branch'}</h5>
                    <p className="text-muted small mb-0">Configure spaces for this location</p>
                </div>
            </div>

            <div className="flex-grow-1 overflow-hidden position-relative" style={{ background: '#f8f9fa' }}>
                {loadingExisting && !existingSpacesMap[activeTab] && (
                    <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 z-20 d-flex flex-column align-items-center justify-content-center">
                        <Spinner color="warning" />
                        <p className="mt-2 text-muted small fw-bold">Loading branch spaces...</p>
                    </div>
                )}
                <AnimatePresence mode="wait">
                    {viewMode === 'list' && (
                        <motion.div
                            key="manager"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="h-100"
                        >
                            {currentSpaces.length === 0 && existingSpaces.length === 0 ? (
                                <EmptyState
                                    onGenerate={() => setViewMode('generate')}
                                    existingCount={existingSpaces.length}
                                    existingSpaces={existingSpaces}
                                    onDeleteExisting={handleDeleteExisting}
                                // nextSite={nextSite}
                                // onSwitchToNext={handleSwitchToNext}
                                />
                            ) : (
                                <SpaceManager
                                    spaces={currentSpaces}
                                    existingSpaces={existingSpaces}
                                    onAdd={() => setViewMode('generate')}
                                    onDelete={handleDelete}
                                    onUpdate={handleUpdate}
                                    onDeleteExisting={handleDeleteExisting}
                                    onUpdateExisting={handleUpdateExisting}
                                // nextSite={nextSite}
                                // onSwitchToNext={handleSwitchToNext}
                                />
                            )}
                        </motion.div>
                    )}

                    {viewMode === 'generate' && (
                        <motion.div
                            key="generate"
                            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="position-absolute top-0 start-0 w-100 h-100 bg-white z-20"
                        >
                            <GenerationView
                                onCancel={() => setViewMode('list')}
                                onConfirm={handleGenerate}
                                siteName={activeSite?.name}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <div className="border-top p-3 bg-white d-flex align-items-center justify-content-between shadow-lg" style={{ zIndex: 30 }}>
                <div className="d-flex align-items-center gap-3">
                    <div>
                        <span className="text-muted small d-block uppercase fw-bold" style={{ fontSize: '10px' }}>To Create</span>
                        <span className="fw-bold fs-5" style={{ color: THEME_COLOR }}>+{totalCount}</span>
                    </div>
                    {limitInfo && (
                        <div className="border-start ps-3 d-none d-md-block" style={{ minWidth: '150px' }}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="text-muted small uppercase fw-bold" style={{ fontSize: '10px' }}>Spaces Usage</span>
                                <span className={`small fw-bold ${isOverLimit ? 'text-danger' : 'text-muted'}`} style={{ fontSize: '10px' }}>
                                    {totalInSpaces} / {limitInfo.limit}
                                </span>
                            </div>
                            <Progress
                                value={limitInfo.limit === 'Unlimited' ? 0 : (totalInSpaces / limitInfo.limit) * 100}
                                style={{ height: '4px' }}
                                barStyle={{ backgroundColor: isOverLimit ? '#f06548' : THEME_COLOR }}
                                className="rounded-pill"
                            />
                        </div>
                    )}
                </div>
                <div className="d-flex gap-2">
                    <Button color="white" className="border-0 text-muted" onClick={handleBackInternal}>Back</Button>
                    <Button
                        className="rounded-pill px-4 shadow-sm fw-bold border-0 text-white"
                        style={{ backgroundColor: THEME_COLOR }}
                        onClick={handleFinalize}
                        disabled={submitting || (totalCount === 0 && !hasExistingSpaces) || isOverLimit}
                    >
                        {isOverLimit ? 'Limit Exceeded' : submitting ? 'Processing...' : 'Complete Setup'} <ArrowRight size={16} className="ms-2" />
                    </Button>
                </div>
            </div>

            <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                .hover-bg-light:hover { background-color: #f8f9fa; }
                .hover-opacity-100:hover { opacity: 1 !important; }
                .hover-up:hover { transform: translateY(-2px); }
            `}</style>
        </div>
    );
};

export default DistributionStep;
